<template>
	<v-dialog
		v-model="dialog"
		max-width="800px"
		:fullscreen="$vuetify.breakpoint.xs ? true : false"
		:transition="$vuetify.breakpoint.xs ? 'scroll-y-reverse-transition' : ''"
	>
		<template v-slot:activator="{ on, attrs }">
			<v-btn
				:text="text"
				:color="text ? 'white' : 'secondary'"
				class="ml-2"
				@click.stop="openDialog"
				v-bind="attrs"
				v-on="on"
			>
				<v-icon class="mr-1">mdi-pencil</v-icon>
				情報物件を編集する
			</v-btn>
		</template>

		<v-card tile v-if="matter">
			<v-app-bar dense dark flat color="primary">
				<v-app-bar-title class="color-white">情報物件編集</v-app-bar-title>
				<v-spacer />
				<v-btn icon dark @click="dialog = false" color="white">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-app-bar>

			<v-form ref="form" v-model="valid">
				<v-container v-if="editMode == 'single'">
					<v-row no-gutters justify="end">
						<v-btn color="secondary" @click="editMode = 'multiple'"> 全ての枝番号を編集する </v-btn>
					</v-row>
					<v-text-field
						label="枝番号"
						v-model="orderIndividualInfo.sub_number"
						type="number"
						hide-spin-buttons
						step="1"
						class="mt-4"
						outlined
						dense
						:rules="[rules.required, rules.integer]"
					/>
					<v-text-field
						label="個数"
						v-model="orderIndividualInfo.quantity"
						type="number"
						hide-spin-buttons
						step="1"
						class="mt-4"
						outlined
						dense
						disabled
						hint="個数を編集するにはダイアログ内右上の【全ての枝番号を編集する】から編集可能です。"
						persistent-hint
						:rules="[rules.required, rules.integer]"
					/>
					<v-text-field
						label="施工場所"
						class="mt-4"
						v-model="orderIndividualInfo.construction_place"
						outlined
						dense
					/>
					<v-row no-gutters>
						<v-col class="pr-sm-2" cols="12" sm="6">
							<v-menu
								ref="menu"
								close-on-content-click
								transition="scale-transition"
								offset-y
								min-width="auto"
							>
								<template v-slot:activator="{ on, attrs }">
									<v-text-field
										label="製造開始日"
										v-model="orderIndividualInfo.start_date"
										outlined
										dense
										readonly
										v-bind="attrs"
										v-on="on"
										class="mt-4"
										:rules="orderIndividualInfo.start_time ? [rules.required] : []"
									/>
								</template>
								<v-date-picker
									v-model="orderIndividualInfo.start_date"
									no-title
									scrollable
									locale="ja"
								/>
							</v-menu>
						</v-col>
						<v-col class="pl-sm-2" cols="12" sm="6">
							<v-text-field
								v-model="orderIndividualInfo.start_time"
								label="製造開始時刻"
								class="mt-4"
								type="time"
								outlined
								dense
							/>
						</v-col>
					</v-row>
					<v-row no-gutters>
						<v-col class="pr-sm-2" cols="12" sm="6">
							<v-menu
								ref="menu"
								close-on-content-click
								transition="scale-transition"
								offset-y
								min-width="auto"
							>
								<template v-slot:activator="{ on, attrs }">
									<v-text-field
										label="製造完了日"
										v-model="orderIndividualInfo.end_date"
										outlined
										dense
										readonly
										v-bind="attrs"
										v-on="on"
										class="mt-4"
										:rules="orderIndividualInfo.end_time ? [rules.required] : []"
									/>
								</template>
								<v-date-picker
									v-model="orderIndividualInfo.end_date"
									no-title
									scrollable
									locale="ja"
								/>
							</v-menu>
						</v-col>
						<v-col class="pl-sm-2" cols="12" sm="6">
							<v-text-field
								v-model="orderIndividualInfo.end_time"
								label="製造完了時刻"
								class="mt-4"
								type="time"
								outlined
								dense
							/>
						</v-col>
					</v-row>
					<v-textarea outlined dense v-model="reason" class="mt-4" label="変更理由" />
				</v-container>

				<v-container v-else-if="editMode == 'multiple'">
					<v-row no-gutters justify="end">
						<v-btn color="secondary" @click="editMode = 'single'">
							現在の枝番号のみを編集する
						</v-btn>
					</v-row>
					<v-sheet elevation="0">
						<h6 class="text-body-1 font-weight-bold">一括設定</h6>
						<div>
							{{ productName() }}
						</div>
						<div>個数：{{ quantity() }}</div>
						<v-select
							label="枝番号の付け方"
							:items="branchNumberOptions"
							v-model="branchNumber"
							item-value="id"
							item-text="name"
							outlined
							dense
							@change="changeOption"
							class="mt-4"
						/>
						<v-text-field
							label="施工場所"
							v-model="constructionPlace"
							class="mt-4"
							outlined
							dense
							hint="施工場所を設定すると全ての枝番号に反映されます。"
							persistent-hint
							@blur="setConstructionPlace"
						/>
						<v-row no-gutters>
							<v-col class="pr-sm-2" cols="12" sm="6">
								<v-menu
									ref="menu"
									close-on-content-click
									transition="scale-transition"
									offset-y
									min-width="auto"
								>
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											label="製造開始日"
											hint="製造開始日を設定すると全ての枝番号に反映されます。"
											v-model="startDate"
											outlined
											dense
											readonly
											v-bind="attrs"
											v-on="on"
											class="mt-4"
										/>
									</template>
									<v-date-picker
										v-model="startDate"
										no-title
										scrollable
										locale="ja"
										@change="setStartDate"
									/>
								</v-menu>
							</v-col>
							<v-col class="pl-sm-2" cols="12" sm="6">
								<v-text-field
									v-model="startTime"
									label="製造開始時刻"
									hint="製造開始時刻を設定すると全ての枝番号に反映されます。"
									class="mt-4"
									type="time"
									outlined
									dense
									@change="setStartTime"
								/>
							</v-col>
						</v-row>
						<v-row no-gutters>
							<v-col class="pr-sm-2" cols="12" sm="6">
								<v-menu
									ref="menu"
									close-on-content-click
									transition="scale-transition"
									offset-y
									min-width="auto"
								>
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											label="製造完了日"
											hint="製造完了日を設定すると全ての枝番号に反映されます。"
											v-model="endDate"
											outlined
											dense
											readonly
											v-bind="attrs"
											v-on="on"
											class="mt-4"
										/>
									</template>
									<v-date-picker
										v-model="endDate"
										no-title
										scrollable
										locale="ja"
										@change="setEndDate"
									/>
								</v-menu>
							</v-col>
							<v-col class="pl-sm-2" cols="12" sm="6">
								<v-text-field
									v-model="endTime"
									label="製造完了時刻"
									hint="製造完了時刻を設定すると全ての枝番号に反映されます。"
									class="mt-4"
									type="time"
									outlined
									dense
									@change="setEndTime"
								/>
							</v-col>
						</v-row>

						<template v-if="orderIndividualInfos.length">
							<v-row no-gutters justify="space-between">
								<h6 class="text-body-1 font-weight-bold">個別設定</h6>
								<v-btn color="secondary" @click="addIndividualInfo">
									<v-icon class="mr-1">mdi-plus</v-icon>
									追加
								</v-btn>
							</v-row>
							<div>
								<div
									v-for="(orderIndividualInfo, i) in orderIndividualInfos"
									:key="i"
									class="py-4 top-divider"
								>
									<v-row no-gutters justify="space-between">
										<h6 class="text-body-1 font-weight-bold">{{ i + 1 }}個目</h6>
										<v-menu v-if="orderIndividualInfo.id" top>
											<template v-slot:activator="{ on, attrs }">
												<v-btn
													icon
													v-bind="attrs"
													v-on="on"
													color="red"
													:disabled="orderIndividualInfos.length < 2"
													class="mt-0"
													><v-icon>mdi-close-circle-outline</v-icon></v-btn
												>
											</template>
											<v-card class="top-border pa-2 pt-4">
												<p>
													データに関連する依頼や物流情報がある場合は、不具合が生じる場合があります。
												</p>
												<v-row justify="center" no-gutters class="mt-4">
													<v-btn color="red" dark @click="removeIndividualInfo(i)">
														<v-icon class="mr-1">mdi-delete</v-icon>
														削除
													</v-btn>
												</v-row>
											</v-card>
										</v-menu>
										<v-btn
											v-else
											icon
											@click="removeIndividualInfo(i)"
											color="red"
											:disabled="orderIndividualInfos.length < 2"
											class="mt-0"
											><v-icon>mdi-close-circle-outline</v-icon></v-btn
										>
									</v-row>
									<v-row no-gutters justify="space-between">
										<v-col cols="6" class="px-1">
											<v-text-field
												label="枝番号"
												v-model="orderIndividualInfo.sub_number"
												type="number"
												hide-spin-button
												step="1"
												outlined
												dense
												:rules="[rules.required, rules.integer]"
											/>
										</v-col>
										<v-col cols="6" class="px-1">
											<v-text-field
												label="個数"
												v-model="orderIndividualInfo.quantity"
												type="number"
												hide-spin-buttons
												step="1"
												outlined
												dense
												class="append-outer"
												:rules="[rules.required, rules.integer, quantityAmountCheck]"
											/>
										</v-col>
									</v-row>
									<v-text-field
										label="施工場所"
										class="mt-4"
										v-model="orderIndividualInfo.construction_place"
										outlined
										dense
									/>
									<v-row no-gutters>
										<v-col class="pr-sm-2" cols="12" sm="6">
											<v-menu
												ref="menu"
												close-on-content-click
												transition="scale-transition"
												offset-y
												min-width="auto"
											>
												<template v-slot:activator="{ on, attrs }">
													<v-text-field
														label="製造開始日"
														v-model="orderIndividualInfo.start_date"
														outlined
														dense
														readonly
														v-bind="attrs"
														v-on="on"
														class="mt-4"
														:rules="orderIndividualInfo.start_time ? [rules.required] : []"
													/>
												</template>
												<v-date-picker
													v-model="orderIndividualInfo.start_date"
													no-title
													scrollable
													locale="ja"
												/>
											</v-menu>
										</v-col>
										<v-col class="pl-sm-2" cols="12" sm="6">
											<v-text-field
												v-model="orderIndividualInfo.start_time"
												label="製造開始時刻"
												class="mt-4"
												type="time"
												outlined
												dense
											/>
										</v-col>
									</v-row>
									<v-row no-gutters>
										<v-col class="pr-sm-2" cols="12" sm="6">
											<v-menu
												ref="menu"
												close-on-content-click
												transition="scale-transition"
												offset-y
												min-width="auto"
											>
												<template v-slot:activator="{ on, attrs }">
													<v-text-field
														label="製造完了日"
														v-model="orderIndividualInfo.end_date"
														outlined
														dense
														readonly
														v-bind="attrs"
														v-on="on"
														class="mt-4"
														:rules="orderIndividualInfo.end_time ? [rules.required] : []"
													/>
												</template>
												<v-date-picker
													v-model="orderIndividualInfo.end_date"
													no-title
													scrollable
													locale="ja"
												/>
											</v-menu>
										</v-col>
										<v-col class="pl-sm-2" cols="12" sm="6">
											<v-text-field
												v-model="orderIndividualInfo.end_time"
												label="製造完了時刻"
												class="mt-4"
												type="time"
												outlined
												dense
											/>
										</v-col>
									</v-row>
								</div>
							</div>
							<p class="text-right">合計: {{ quantityAmount }}</p>
						</template>
					</v-sheet>
					<v-divider />
					<v-textarea outlined dense v-model="reason" class="mt-4" label="変更理由" />
				</v-container>
			</v-form>
			<v-card-actions>
				<v-row no-gutters class="pb-2">
					<v-col cols="0" sm="3" />
					<v-col cols="12" sm="6">
						<v-row no-gutters justify="center">
							<v-btn color="secondary" @click="submitForm" :disabled="!valid">
								<v-icon class="mr-2">mdi-plus</v-icon>
								登録する
							</v-btn>
						</v-row>
					</v-col>
					<v-col cols="0" sm="3">
						<v-row no-gutters justify="end">
							<v-btn color="grey darken-1" text @click="dialog = false"> 閉じる </v-btn>
						</v-row>
					</v-col>
				</v-row>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import axios from "axios"
import moment from "moment/min/moment.min.js"
import rules from "../../../helper/rules"
import common from "../../../helper/common"
import errorHandling from "../../../helper/errorHandling"
import authenticate from "../../../helper/authenticate"
import PaginationModule from "../../shared/PaginationModule.vue"
import OrderIndividualInfoForm from "./OrderIndividualInfoForm.vue"
import MatterBriefInfo from "./MatterBriefInfo.vue"

export default {
	props: {
		text: Boolean,
		id: {
			type: null,
			required: true,
		},
	},
	components: {
		PaginationModule,
		OrderIndividualInfoForm,
		MatterBriefInfo,
	},
	filters: {
		dateFormatter(date) {
			if (!date) return ""
			return moment(date).format("YYYY/MM/DD")
		},
	},
	data() {
		return {
			matter: null,
			dialog: false,
			valid: false,
			ordered_product: null,
			matter_product: null,
			editMode: "single",
			orderIndividualInfos: [],
			orderIndividualInfo: {
				sub_number: null,
				quantity: null,
				construction_place: null,
				start_date: null,
				end_date: null,
				start_time: null,
				end_time: null,
			},
			rules: rules,
			pagination: {
				count: null,
				current: 1,
				limit_value: null,
				next: null,
				pages: null,
				previous: null,
			},
			deletedIds: [],
			divideByNumber: null,
			divideIntoNumber: null,
			branchNumber: null,
			division: null,
			constructionPlace: null,
			startDate: null,
			startTime: null,
			endDate: null,
			endTime: null,
			reason: null,
			branchNumberOptions: [
				{ id: "auto", name: "1から順番で自動で設定する" },
				{ id: "original", name: "自身で設定する" },
			],
			rules: rules,
			common: common,
		}
	},
	// mounted() {
	// 	this.openDialog()
	// },
	methods: {
		openDialog() {
			this.fetchIndividualInfos()
		},
		fetchIndividualInfos() {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/order_individual_infos/${this.id}/edit`
			axios
				.get(url, { headers })
				.then((res) => {
					this.dialog = true
					this.matter = res.data.matter
					this.matter_product = res.data.matter_product
					this.ordered_product = res.data.ordered_product
					this.orderIndividualInfos = this.setOrderIndividualInfos(
						this.ordered_product ? this.ordered_product : this.matter_product
					)
					this.orderIndividualInfo = this.orderIndividualInfos.find((o) => o.id == this.id)
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
		submitForm() {
			const headers = authenticate.setHeader()
			const url = `/erp/v1/order_individual_infos/${this.id}`
			let params = {}
			if (this.editMode == "multiple") {
				const order_individual_info = this.orderIndividualInfos.map((info) => ({
					id: info.id,
					construction_place: info.construction_place,
					sub_number: info.sub_number,
					start_date: info.start_date,
					start_time: info.start_time,
					end_date: info.end_date,
					end_time: info.end_time,
				}))
				params = {
					order_individual_info,
					deleted_ids: this.deletedIds,
					reason: this.reason,
					edit_mode: this.editMode,
				}
			} else if (this.editMode == "single") {
				params = {
					order_individual_info: {
						construction_place: this.orderIndividualInfo.construction_place,
						sub_number: this.orderIndividualInfo.sub_number,
						start_date: this.orderIndividualInfo.start_date,
						start_time: this.orderIndividualInfo.start_time,
						end_date: this.orderIndividualInfo.end_date,
						end_time: this.orderIndividualInfo.end_time,
					},
					reason: this.reason,
					edit_mode: this.editMode,
				}
			}
			axios
				.put(url, params, { headers })
				.then((res) => {
					if (this.editMode == "multiple") {
						this.$emit("reset-order-individual-info")
					} else if (this.editMode == "single") {
						this.$emit("update-individual-info", res.data)
					}
					this.$store.dispatch("setFlash", {
						type: "notice",
						text: "変更を保存しました。",
					})
					this.reason = null
					this.dialog = false
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
		changeOption() {
			if (this.branchNumber == "auto") {
				this.orderIndividualInfos.forEach((o, i) => {
					o.sub_number = this.setSubNumber(i)
				})
			}
		},
		addIndividualInfo() {
			const sub_number =
				this.orderIndividualInfos[this.orderIndividualInfos.length - 1]?.sub_number || 0
			this.orderIndividualInfos.push({
				sub_number: this.setSubNumber(sub_number),
				quantity: null,
				...this.setProductId(),
			})
		},
		removeIndividualInfo(i) {
			const individualInfo = this.orderIndividualInfos[i]
			if (individualInfo.id) this.deletedIds.push(individualInfo.id)
			this.orderIndividualInfos.splice(i, 1)
		},
		setOrderIndividualInfos(product) {
			return product.order_individual_infos
				.filter((i) => !i.destroy_flag)
				.map((o) => {
					o.start_time = common.formatTime(o.start_time)
					o.end_time = common.formatTime(o.end_time)
					return o
				})
		},
		setConstructionPlace() {
			this.orderIndividualInfos.forEach((o) => {
				o.construction_place = this.constructionPlace
			})
		},
		setStartDate() {
			this.orderIndividualInfos.forEach((o) => {
				o.start_date = this.startDate
			})
		},
		setStartTime() {
			this.orderIndividualInfos.forEach((o) => {
				o.start_time = this.startTime
			})
		},
		setEndDate() {
			this.orderIndividualInfos.forEach((o) => {
				o.end_date = this.endDate
			})
		},
		setEndTime() {
			this.orderIndividualInfos.forEach((o) => {
				o.end_time = this.endTime
			})
		},
		setSubNumber(i) {
			if (this.branchNumber == "auto") {
				return i + 1
			} else if (this.branchNumber == "original") {
				return null
			}
		},
		setProductId() {
			if (this.ordered_product) {
				return {
					ordered_product_id: this.ordered_product.id,
					construction_place: this.constructionPlace,
					start_date: this.startDate,
					start_time: this.startTime,
					end_date: this.endDate,
					end_time: this.endTime,
				}
			} else if (this.matter_product) {
				return {
					matter_product_id: this.matter_product.id,
					construction_place: this.constructionPlace,
					start_date: this.startDate,
					start_time: this.startTime,
					end_date: this.endDate,
					end_time: this.endTime,
				}
			}
		},
		quantity() {
			if (this.ordered_product) {
				return this.ordered_product.quantity
			} else if (this.matter_product) {
				return this.matter_product.quantity
			}
		},
		productName() {
			if (this.ordered_product) {
				return `${this.ordered_product.product.name} ${
					this.ordered_product.sub_name ? this.ordered_product.sub_name : ""
				}`
			} else if (this.matter_product) {
				return `${this.matter_product.product.name} ${
					this.matter_product.sub_name ? this.matter_product.sub_name : ""
				}`
			}
		},
	},
	computed: {
		quantityAmountCheck() {
			const amount = this.orderIndividualInfos.reduce(
				(preVal, newVal) =>
					preVal + (isNaN(parseInt(newVal.quantity)) ? 0 : parseInt(newVal.quantity)),
				0
			)
			if (amount != this.quantity()) return "合計個数が一致しません。"
			return true
		},
		quantityAmount() {
			const amount = this.orderIndividualInfos.reduce(
				(preVal, newVal) =>
					preVal + (isNaN(parseInt(newVal.quantity)) ? 0 : parseInt(newVal.quantity)),
				0
			)
			return amount
		},
	},
}
</script>


<style lang="scss" scoped>
.products {
	.row {
		&:not(:first-child) {
			border-top: 1px solid whitesmoke;
		}
	}
}
</style>